import type { CharacterSpec } from '@cuboom/shared';
import { blip, chirp, noiseBurst, thump, voiceBlab } from './synth.js';
import { MusicLoop } from './music.js';

const LS_VOL = 'cuboom.vol';
const LS_MUTE = 'cuboom.mute';
const LS_MUSIC = 'cuboom.music';

/**
 * WebAudio hub: lazy context (created + resumed on the first user gesture —
 * browsers refuse audio before one), category buses with persisted volumes,
 * a small voice-stealing budget, and the music loop.
 *
 * Everything degrades silently: no context yet (or autoplay refused) simply
 * means no sound, never a throw.
 */
export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private voiceBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private music: MusicLoop | null = null;

  private muted = false;
  private musicOn = true;
  private volSfx = 0.85;
  private volMusic = 0.35;
  private volVoice = 0.9;

  /** Budgets: oldest voice is stolen when the bus is saturated. */
  private liveSfx = 0;
  private liveVoice = 0;

  constructor() {
    this.loadPrefs();
    // Unlock on the first gesture of any kind (spec: audio starts on tap).
    const unlock = (): void => {
      this.ensure();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('touchstart', unlock);
  }

  // --- setup ---------------------------------------------------------------

  private ensure(): AudioContext | null {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return this.ctx;
    }
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = this.muted ? 0 : 1;
      master.connect(ctx.destination);
      const mk = (v: number): GainNode => {
        const g = ctx.createGain();
        g.gain.value = v;
        g.connect(master);
        return g;
      };
      this.ctx = ctx;
      this.master = master;
      this.sfxBus = mk(this.volSfx);
      this.voiceBus = mk(this.volVoice);
      this.musicBus = mk(this.volMusic);
      this.music = new MusicLoop(ctx, this.musicBus);
      if (this.musicOn) this.music.start(); // respect the persisted power switch
      return ctx;
    } catch {
      return null; // autoplay policy or no WebAudio — play silent
    }
  }

  private loadPrefs(): void {
    try {
      const raw = localStorage.getItem(LS_VOL);
      if (raw) {
        const v = JSON.parse(raw) as { sfx?: number; music?: number; voice?: number };
        this.volSfx = v.sfx ?? this.volSfx;
        this.volMusic = v.music ?? this.volMusic;
        this.volVoice = v.voice ?? this.volVoice;
      }
      this.muted = localStorage.getItem(LS_MUTE) === '1';
      this.musicOn = localStorage.getItem(LS_MUSIC) !== '0'; // absent = on
    } catch {
      /* private mode — defaults are fine */
    }
  }

  private savePrefs(): void {
    try {
      localStorage.setItem(LS_VOL, JSON.stringify({ sfx: this.volSfx, music: this.volMusic, voice: this.volVoice }));
      localStorage.setItem(LS_MUTE, this.muted ? '1' : '0');
      localStorage.setItem(LS_MUSIC, this.musicOn ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  // --- settings (settings menu binds these) --------------------------------

  setMuted(on: boolean): void {
    this.muted = on;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(on ? 0 : 1, this.ctx.currentTime, 0.03);
    this.savePrefs();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setVolume(cat: 'sfx' | 'music' | 'voice', v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    if (cat === 'sfx') this.volSfx = clamped;
    else if (cat === 'music') this.volMusic = clamped;
    else this.volVoice = clamped;
    const bus = cat === 'sfx' ? this.sfxBus : cat === 'music' ? this.musicBus : this.voiceBus;
    if (bus && this.ctx) bus.gain.setTargetAtTime(clamped, this.ctx.currentTime, 0.03);
    this.savePrefs();
  }

  getVolume(cat: 'sfx' | 'music' | 'voice'): number {
    return cat === 'sfx' ? this.volSfx : cat === 'music' ? this.volMusic : this.volVoice;
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /**
   * Background music power switch (menu row + HUD button). Works before the
   * context exists too — ensure() reads the flag when it first starts the loop.
   */
  setMusicOn(on: boolean): void {
    this.musicOn = on;
    if (this.music) {
      if (on) this.music.start();
      else this.music.stop();
    }
    this.savePrefs();
  }

  get isMusicOn(): boolean {
    return this.musicOn;
  }

  toggleMusic(): boolean {
    this.setMusicOn(!this.musicOn);
    return this.musicOn;
  }

  // --- helpers -------------------------------------------------------------

  private sfx(): { ctx: AudioContext; bus: GainNode } | null {
    const ctx = this.ensure();
    if (!ctx || !this.sfxBus) return null;
    if (this.liveSfx >= 8) return null; // soft cap: drop rather than steal
    this.liveSfx++;
    window.setTimeout(() => this.liveSfx--, 900);
    return { ctx, bus: this.sfxBus };
  }

  // --- game SFX ------------------------------------------------------------

  /** Explosion scaled by blast radius: noise sweep + sine thump. */
  explosion(r: number): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    const k = Math.min(1, r / 130); // size factor
    noiseBurst(ctx, bus, t, { dur: 0.35 + k * 0.4, gain: 0.75, f0: 3800, f1: 110 });
    thump(ctx, bus, t, { f0: 90 + 50 * k, f1: 38, dur: 0.35 + k * 0.3, gain: 0.85 });
    if (this.music) this.music.duck(0.35, 0.12);
  }

  /** Impact bounce — pitch by impact speed. */
  bounce(speed: number): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    blip(ctx, bus, ctx.currentTime, {
      freq: 180 + Math.min(500, speed * 1.4),
      slide: 90,
      dur: 0.07,
      gain: 0.2,
      type: 'sine',
    });
  }

  /** Firing the slingshot. */
  fire(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    noiseBurst(ctx, bus, t, { dur: 0.12, gain: 0.3, f0: 900, f1: 2600, type: 'bandpass', q: 1.4 });
    blip(ctx, bus, t + 0.03, { freq: 340, slide: 620, dur: 0.1, gain: 0.22, type: 'triangle' });
  }

  /** Weapon impact that is not (yet) an explosion — wet thud. */
  splat(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    noiseBurst(ctx, bus, t, { dur: 0.16, gain: 0.4, f0: 1500, f1: 350, type: 'lowpass' });
    blip(ctx, bus, t, { freq: 240, slide: 70, dur: 0.12, gain: 0.3, type: 'sine' });
  }

  splash(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    noiseBurst(ctx, bus, t, { dur: 0.4, gain: 0.5, f0: 2400, f1: 500, type: 'bandpass', q: 0.9 });
    blip(ctx, bus, t, { freq: 520, slide: 130, dur: 0.22, gain: 0.18, type: 'sine' });
  }

  swap(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    chirp(ctx, bus, t, { f0: 600, f1: 1200, dur: 0.14, gain: 0.2 });
    chirp(ctx, bus, t + 0.09, { f0: 1100, f1: 500, dur: 0.16, gain: 0.18 });
  }

  death(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    thump(ctx, bus, t, { f0: 420, f1: 60, dur: 0.5, gain: 0.5 });
    noiseBurst(ctx, bus, t, { dur: 0.4, gain: 0.3, f0: 2200, f1: 200, type: 'lowpass' });
  }

  /** UI tap on a toolbar icon / menu item. */
  uiTap(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    blip(ctx, bus, ctx.currentTime, { freq: 660, dur: 0.05, gain: 0.16, type: 'triangle' });
  }

  /** Denied intent — a soft buzz. */
  deny(): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    blip(ctx, bus, ctx.currentTime, { freq: 160, dur: 0.14, gain: 0.25, type: 'square' });
    blip(ctx, bus, ctx.currentTime + 0.07, { freq: 130, dur: 0.16, gain: 0.22, type: 'square' });
  }

  /** Match-end stinger: rising triad on a win, descending on a draw. */
  matchEnd(decisive: boolean): void {
    const s = this.sfx();
    if (!s) return;
    const { ctx, bus } = s;
    const t = ctx.currentTime;
    const seq = decisive ? [392, 523.25, 659.25, 784] : [523.25, 440, 349.23, 261.63];
    seq.forEach((f, i) => {
      blip(ctx, bus, t + i * 0.13, { freq: f, dur: 0.24, gain: 0.26, type: 'triangle' });
    });
    if (decisive) thump(ctx, bus, t + 0.52, { f0: 160, f1: 50, dur: 0.4, gain: 0.5 });
    if (this.music) this.music.duck(0.4, 0.15);
  }

  /** Character voice blab — the species' synthesized gibberish. */
  voice(spec: CharacterSpec, mood: 'chirp' | 'hurt' | 'die' | 'happy'): void {
    const ctx = this.ensure();
    if (!ctx || !this.voiceBus) return;
    if (this.liveVoice >= 3) return;
    this.liveVoice++;
    window.setTimeout(() => this.liveVoice--, 700);
    voiceBlab(ctx, this.voiceBus, ctx.currentTime, {
      pitch: spec.voice.pitch,
      formant: spec.voice.formant,
      rasp: spec.voice.rasp,
      mood,
    });
  }
}
